import { useEffect, useRef, RefObject } from "react";

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([aria-hidden="true"]),textarea,select,[tabindex]:not([tabindex="-1"])';

// Moves focus into a dialog when it opens and restores it to the triggering
// element when it closes. Pair with role="dialog" aria-modal="true" and
// tabIndex={-1} on the container `ref`.
//
// `initialFocus` narrows what receives focus on open (e.g. the contact form's
// first input); it falls back to the dialog's first focusable element.
//
// Touch-first devices (pointer: coarse) always focus the dialog CONTAINER
// instead: focusing an input pops the keyboard (and zooms on iOS) before the
// dialog has even settled. The dialog is presented from its top — its own
// scroll and any Radix scroll-area viewports inside are reset.
export const useDialogFocus = (
  ref: RefObject<HTMLElement | null>,
  open: boolean,
  initialFocus: string = FOCUSABLE
) => {
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      triggerRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      const coarse = window.matchMedia("(pointer: coarse)").matches;
      const target = coarse
        ? ref.current
        : (ref.current?.querySelector<HTMLElement>(initialFocus) ??
          ref.current);
      // preventScroll: focusing an element inside a portaled (end-of-<body>)
      // dialog otherwise makes the browser scroll the page to "reveal" it.
      target?.focus({ preventScroll: true });
      if (coarse) {
        ref.current?.scrollTo?.(0, 0);
        ref.current
          ?.querySelectorAll<HTMLElement>("[data-radix-scroll-area-viewport]")
          .forEach((el) => el.scrollTo(0, 0));
      }
    } else {
      triggerRef.current?.focus({ preventScroll: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
};
